import React, { useEffect, useState } from "react";
import {
  Button,
  Divider,
  Form,
  Grid,
  Icon,
  Message,
  Radio,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Field, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { useStore } from "../../app/stores/store";
import PageHeader from "../../app/layout/PageHeader";
import { observer } from "mobx-react-lite";
import MyTextInput from "../../app/common/form/MyTextInput";
import { AccountsReceivable, AccountsReceivableFormValues } from "../../app/models/accountsReceivable";
import { yesNoOptions } from "../../app/common/options/option";
import MyIconDecimalInput from "../../app/common/form/MyIconDecimalInput";

interface Props {
  isAdd: boolean;
  accountsReceivable?: AccountsReceivable | undefined;
  addItem: (values: AccountsReceivableFormValues) => void;
  autoCapitalizeFirstLetter: boolean;
  formValues?: AccountsReceivableFormValues;
}

export default observer(function ARForm({
  isAdd,
  accountsReceivable,
  addItem,
  autoCapitalizeFirstLetter,
  formValues,
}: Props) {
  const { t } = useTranslation(["common", "translation", "tickets", "customer"]);

  const { modalStore, vendorStore, accountsReceivableSettingsStore } = useStore();

  const { accountsReceivableSettings } = accountsReceivableSettingsStore;

  const {
    loadDropdownListVendors,
    loadVendors,
  } = vendorStore;

  useEffect(() => {
    loadDropdownListVendors();
  }, [loadDropdownListVendors]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [arForm, setARForm] = useState<AccountsReceivableFormValues>(
    formValues ?? new AccountsReceivableFormValues(accountsReceivable)
  );
  const [active, setActive] = useState<boolean>(accountsReceivable?.isActive ?? false);

  const validationSchema = Yup.object({
    isActive: Yup.boolean(),
    creditLimit: Yup.string().nullable().when('isActive', {
      is: true,
      then: (schema) => schema.required(
        t("accountsReceivable.emptyCreditLimit", {
          ns: "errors",
        })
      ),
      otherwise: (schema) => schema.notRequired()
    }),
  });

  function handleFormSubmit(values: AccountsReceivableFormValues, helpers: FormikHelpers<AccountsReceivableFormValues>) {
    const { setSubmitting } = helpers;
    try {
      setSubmitting(true);

      if(!values.creditLimit) values.creditLimit = accountsReceivableSettings?.defaultMaxCreditLimit ?? 0;

      if(isNaN(values.creditLimit)) {
        helpers.setFieldError("creditLimit", t("accountsReceivable.nanCreditLimit", { ns: "errors" }));
        return;
      }

      if (addItem)
        addItem(values);

      modalStore.closeModal();
    } finally {
      setSubmitting(false);
    }
  }

  function valuesChanged(values: AccountsReceivableFormValues) {
    const isActive = String(values.isActive) === "true" ? true : false;
    return Number(values.creditLimit) !== arForm.creditLimit || values.accountNumber !== arForm.accountNumber || isActive !== arForm.isActive; 
  }

  return (
    <>
      <PageHeader
        type={"h3"}
        header={accountsReceivable?.id ? t("editAccountsReceivable_title", { ns: "customer" }) : t("addAccountsReceivable_title", { ns: "customer" })}
        divider={true}
        addTitle={false}
      />
      <Formik
        validationSchema={validationSchema}
        initialValues={arForm}
        onSubmit={(values: AccountsReceivableFormValues, helpers: FormikHelpers<AccountsReceivableFormValues>) => handleFormSubmit(values, helpers)}
      >
        {({
          setFieldValue,
          setFieldError,
          handleSubmit,
          handleBlur,
          handleChange,
          isSubmitting,
          errors,
          values
        }) => (
          <Form
            className="ui form error"
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            <Grid stackable>
              <Grid.Row columns={2}>
              {(accountsReceivable?.balanceDue ?? 0) > 0 && !active && (
                <Message error>
                  {t('balanceDueAR', { ns: 'customer' })}
                </Message>
              )}
                <Grid.Column verticalAlign="top">
                  <MyIconDecimalInput
                    placeholder={t("creditLimit", { ns: "accountsReceivable" })}
                    label={t("creditLimit", { ns: "accountsReceivable" }) + ":"}
                    className="full-width"
                    name="creditLimit"
                    maxLength={50} 
                    icon="dollar"
                    iconPosition="left"
                  />
                </Grid.Column>
                <Grid.Column>
                  <MyTextInput
                    placeholder={t("accountNumber", { ns: "accountsReceivable" })}
                    label={t("accountNumber", { ns: "accountsReceivable" }) + ":"}
                    className="full-width"
                    name="accountNumber"
                    autoCapitalizeFirstLetter={autoCapitalizeFirstLetter}
                    maxLength={1000}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <label className="required">
                    {`${t("active", {
                      ns: "accountsReceivable",
                    })}:`}
                  </label>
                  {yesNoOptions.map((data, index) => (
                    <Form.Field key={`isActive[${data.value}]`} inline>
                      <Radio
                        {...Field}
                        id={`isActive[${index}]`}
                        key={`isActive[${data.value}]`}
                        value={data.value}
                        name="isActive"
                        onChange={(e, data) => { 
                          const active = data.value === "true";
                          if(active && !values.creditLimit) {
                            const defaultMaxCreditLimit = accountsReceivableSettings?.defaultMaxCreditLimit;
                            if(defaultMaxCreditLimit) {
                              setFieldValue("creditLimit", defaultMaxCreditLimit);
                              setFieldError("creditLimit", undefined);
                            }
                          }
                          setActive(active);
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        label={data.text}
                        className="myLabel"
                        checked={values.isActive.toString() === data.value}
                      />
                    </Form.Field>
                  ))}
                </Grid.Column>
              </Grid.Row>
              <Divider />
              <Grid.Row>
                <Grid.Column>
                  <Button
                    loading={isSubmitting}
                    disabled={isSubmitting || !valuesChanged(values)}
                    color="green"
                    type="submit"
                  >
                    <Icon name="check" />
                    {accountsReceivable?.id ? t("save", { ns: "common" }) : t("add", { ns: "common" })}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      modalStore.closeModal();
                    }}
                  >
                    <Icon name="x" />
                    {t("cancel", { ns: "common" })}
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
});
